import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Modal, TextField, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { FiRotateCcw } from "react-icons/fi";

import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { TaskInterface } from "../../common/interfaces";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import { TypeTaskStatus } from "../enum/taskStatusEnum";
import PaginationControls from "../PaginationControls";
import PageToaster from "../toaster/PageToaster";
import { EmptyList, RowButton, TaskRow, TaskRowList } from "./TaskListModal.styled";

interface TaskCompletedModalProps {
  open: boolean;
  onClose: () => void;
  /** Avisa a página para atualizar o quadro ao reabrir uma tarefa daqui. */
  onChanged?: () => void;
  /** Só tem efeito para ADMIN: mesma empresa escolhida no quadro. */
  companyId?: number;
}

const PAGE_SIZE = 8;

/**
 * Tarefas concluídas e já aprovadas por manager/owner/admin — elas saem do
 * quadro assim que aprovadas e só podem ser vistas aqui. Quem aprova também
 * pode reabri-las de volta para "Em Progresso".
 */
const TaskCompletedModal: React.FC<TaskCompletedModalProps> = ({
  open,
  onClose,
  onChanged,
  companyId
}) => {
  const [tasks, setTasks] = useState<TaskInterface[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  const { isAdmin, isManager, isOwner } = useAuth();
  const canReopen = isAdmin || isManager || isOwner;

  const containerId = "toaster-task-completed-modal";
  const { autoSuccess, autoError } = usePageToast(containerId, true);

  const handleHttpError = makeToasterErrorHandler(autoError);

  const fetchTasks = useCallback(
    async (currentPage: number, currentSearch: string) => {
      setLoading(true);
      try {
        // Só as já aprovadas: a tarefa entra nesta lista quando sai do
        // quadro, seja por aprovação manual ou por vencer o prazo.
        const response = await api.get(`/task`, {
          params: {
            page: currentPage,
            limit: PAGE_SIZE,
            task_status: TypeTaskStatus.DONE,
            archived: "true",
            search: currentSearch.trim() || undefined,
            company_id: companyId
          }
        });

        setTasks(response.data.items);
        setTotalPages(response.data.meta.totalPages || 1);
      } catch (error) {
        console.error("Erro ao buscar tarefas concluídas:", error);
        handleHttpError(error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyId]
  );

  const handleReopen = async (task: TaskInterface) => {
    setBusyId(task.task_id);
    try {
      await api.patch(`/task/${task.task_id}/to-progress`);
      autoSuccess("Tarefa reaberta e devolvida para Em Progresso!");
      await fetchTasks(page, search);
      onChanged?.();
    } catch (error) {
      console.error("Erro ao reabrir tarefa:", error);
      handleHttpError(error);
    } finally {
      setBusyId(null);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchTasks(page, search);
  }, [open, page, search, fetchTasks]);

  // Sempre reabre na primeira página.
  useEffect(() => {
    if (open) {
      setPage(1);
      setSearch("");
    }
  }, [open]);

  const formatDate = (value: string | null) =>
    value
      ? new Date(value).toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        })
      : "—";

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={onClose} aria-labelledby="modal-task-done">
        <Box
          sx={{
            position: "absolute" as const,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
            width: { xs: "92vw", sm: "640px" },
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Tarefas Concluídas</Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            size="small"
            fullWidth
            label="Buscar por nome"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            sx={{ mb: 2 }}
          />

          <TaskRowList>
            {tasks.map((task) => (
              <TaskRow key={task.task_id} $accent="#34d399">
                <h3>{task.task_title}</h3>
                {task.task_description && <p>{task.task_description}</p>}
                <div className="meta">
                  <span>Data limite: {formatDate(task.task_due_date)}</span>
                  <span>Concluída em: {formatDate(task.completed_at)}</span>
                </div>

                {canReopen && (
                  <div className="row-actions">
                    <RowButton
                      onClick={() => handleReopen(task)}
                      disabled={busyId === task.task_id}
                    >
                      <FiRotateCcw /> Reabrir Tarefa
                    </RowButton>
                  </div>
                )}
              </TaskRow>
            ))}

            {!loading && tasks.length === 0 && (
              <EmptyList>Nenhuma tarefa concluída por aqui ainda.</EmptyList>
            )}
          </TaskRowList>

          {totalPages > 1 && (
            <PaginationControls
              page={page}
              totalPages={totalPages}
              setPage={setPage}
            />
          )}
        </Box>
      </Modal>
    </>
  );
};

export default TaskCompletedModal;
